import React from 'react'

const SHADE_LABELS = {
	light: 'Светлый',
	base: 'Основной',
	dark: 'Тёмный',
}

// Темы приходят с бэка уже отсортированными по order_number (семейство, затем оттенок),
// поэтому порядок семейств и оттенков внутри задаёт сам порядок массива.
export function groupThemesByFamily(themes = []) {
	const families = []
	const byKey = new Map()

	for (const theme of themes) {
		const key = theme.family || 'other'

		if (!byKey.has(key)) {
			const family = { key, name: theme.family_name || 'Прочие', themes: [] }
			byKey.set(key, family)
			families.push(family)
		}

		byKey.get(key).themes.push(theme)
	}

	return families
}

// Тема по умолчанию для нового потребителя: первым в списке идёт светлый оттенок
// семейства, а разумный дефолт — основной.
export function defaultTheme(themes = []) {
	return themes.find(theme => theme.shade === 'base') ?? themes[0] ?? null
}

function ThemeSwatch({ theme, active, onSelect }) {
	const colors = theme.colors ?? {}
	const primary = colors.primary?.default ?? '#e11d48'
	const background = colors.background?.default ?? '#ffffff'
	const card = colors.background?.card ?? '#ffffff'
	const text = colors.text?.default ?? '#111827'
	const muted = colors.text?.muted ?? '#6b7280'

	return (
		<div className="d-flex flex-column align-items-center" style={{ width: 88 }}>
			<button
				type="button"
				onClick={() => onSelect(theme)}
				title={theme.name}
				aria-pressed={active}
				className="p-0 border-0 bg-transparent w-100"
				style={{ cursor: 'pointer' }}>
				<div
					style={{
						borderRadius: 12,
						overflow: 'hidden',
						background,
						boxShadow: active
							// У ч/б тёмной акцент белый — рамка выбора цветом акцента терялась на белой странице
							? `0 0 0 2px #ffffff, 0 0 0 4px ${primary.toLowerCase() === '#ffffff' ? '#111827' : primary}`
							: '0 0 0 1px rgba(15, 23, 42, .15)',
					}}>
					<div style={{ background: card, padding: 8 }}>
						<div style={{ height: 8, width: '72%', borderRadius: 999, background: text }} />
						<div style={{ height: 6, width: '46%', borderRadius: 999, background: muted, marginTop: 5 }} />
					</div>
					<div style={{ padding: 8 }}>
						<div style={{ height: 16, borderRadius: 999, background: primary }} />
					</div>
				</div>
			</button>
			<small className={active ? 'fw-bold mt-1' : 'text-muted mt-1'}>
				{SHADE_LABELS[theme.shade] ?? theme.name}
			</small>
		</div>
	)
}

/**
 * Выбор темы из общего каталога. Используется конструктором сайта и телеграм-ботом,
 * дальше планируется QR-меню стола — поэтому заголовок задаётся снаружи.
 */
function ThemePicker({ themes = [], value = null, onChange, title = 'Тема оформления', required = false }) {
	const families = groupThemesByFamily(themes)
	const selected = value ? themes.find(theme => Number(theme.id) === Number(value)) : null

	return (
		<>
			<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
				<h5 className="mb-0">{title}{required && <span className="required-mark">*</span>}</h5>
				{!!selected &&
					<div className="d-flex flex-wrap align-items-center gap-2">
						<span className="text-muted">Выбрана:</span>
						<b className="me-2">{selected.name}</b>
						{Object.entries(selected.colors ?? {}).map(([key, group]) => (
							<div className="d-flex align-items-center gap-1" key={key}>
								<span
									className="border"
									style={{
										width: 18,
										height: 18,
										borderRadius: 4,
										background: group?.default ?? group?.success ?? 'var(--dark-surface, #fff)',
									}}
								/>
								<small className="text-muted">{key}</small>
							</div>
						))}
					</div>
				}
			</div>
			{families.length
				? <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
					{families.map(family => (
						<div className="col" key={family.key}>
							<small className="text-muted d-block mb-1">{family.name}</small>
							<div className="d-flex flex-wrap gap-2">
								{family.themes.map(theme => (
									<ThemeSwatch
										key={theme.id}
										theme={theme}
										active={Number(theme.id) === Number(value)}
										onSelect={onChange}
									/>
								))}
							</div>
						</div>
					))}
				</div>
				: <div className="text-muted">Темы не найдены</div>
			}
		</>
	)
}

export default ThemePicker
